import { useState } from 'react';
import { submitContactForm } from '@/lib/api/forms';
import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { SmsConsentDisclosure } from '@/components/forms/SmsConsentDisclosure';
import { Honeypot } from '@/components/forms/Honeypot';
import { useHoneypot } from '@/components/forms/use-honeypot';

const CLEANING_TYPES = ['Standard', 'Deep Clean', 'Move-Out', 'Commercial', 'Medical'];
const TIME_SLOTS = [
  { id: 'morning', label: 'MORNING 8AM–12PM' },
  { id: 'afternoon', label: 'AFTERNOON 12PM–4PM' },
  { id: 'evening', label: 'EVENING 4PM–7PM' },
];

export function ScheduleForm() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    cleaningType: 'Standard',
    date: '',
    time: 'morning',
    address: '',
    instructions: '',
  });
  const [smsConsent, setSmsConsent] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const { value: hp, honeypotProps } = useHoneypot();

  const onChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');

    const timeLabel = TIME_SLOTS.find((t) => t.id === form.time)?.label || form.time;
    const message = [
      `[SCHEDULING REQUEST]`,
      `Cleaning Type: ${form.cleaningType}`,
      `Preferred Date: ${form.date}`,
      `Preferred Time: ${timeLabel}`,
      `Address: ${form.address}`,
      form.instructions ? `Special Instructions: ${form.instructions}` : '',
    ].filter(Boolean).join('\n');

    try {
      await submitContactForm(
        {
          name: form.name,
          email: form.email,
          phone: form.phone,
          message,
        },
        'schedule_request',
        smsConsent,
        hp,
      );
      setStatus('sent');
      setForm({ name: '', email: '', phone: '', cleaningType: 'Standard', date: '', time: 'morning', address: '', instructions: '' });
      setSmsConsent(false);
    } catch {
      setStatus('error');
    }
  };

  const inputClass = 'bold-input';
  const radioClass = (active: boolean) =>
    `font-display tracking-wider text-xs px-4 py-3 border-2 cursor-pointer transition text-center ${
      active ? 'border-electric bg-electric/20 text-electric' : 'border-white/20 text-white/60 hover:border-electric/50'
    }`;

  return (
    <section id="schedule" className="relative py-28 bg-charcoal overflow-hidden">
      <div
        className="absolute top-0 left-0 w-1/2 h-full pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at left, rgba(255,214,10,.08), transparent 70%)' }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-10 lg:gap-16">
        <ScrollReveal direction="left">
          <div className="text-thunder font-display tracking-[0.4em] text-xs mb-4">// BOOK IT</div>
          <h2 className="font-display font-bold text-white text-5xl sm:text-6xl leading-[0.9] mb-8">
            SCHEDULE A <span className="text-electric">CLEANING</span>
          </h2>
          <p className="text-white/70 text-lg leading-relaxed mb-10">
            Pick your service, choose a time, and we'll confirm within one hour. No commitment — just a clean space on your schedule.
          </p>

          <div className="space-y-6">
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 border-2 border-thunder flex items-center justify-center text-thunder text-xl skew-card">
                <span className="skew-inner">01</span>
              </div>
              <div>
                <div className="font-display text-white tracking-wider text-sm">FILL OUT THE FORM</div>
                <div className="text-white/50 text-sm">Pick your service type, date, and time.</div>
              </div>
            </div>
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 border-2 border-thunder flex items-center justify-center text-thunder text-xl skew-card">
                <span className="skew-inner">02</span>
              </div>
              <div>
                <div className="font-display text-white tracking-wider text-sm">WE CONFIRM</div>
                <div className="text-white/50 text-sm">A team member locks in your slot within an hour.</div>
              </div>
            </div>
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 border-2 border-thunder flex items-center justify-center text-thunder text-xl skew-card">
                <span className="skew-inner">03</span>
              </div>
              <div>
                <div className="font-display text-white tracking-wider text-sm">WE SHOW UP</div>
                <div className="text-white/50 text-sm">On time. In uniform. Ready to work.</div>
              </div>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal direction="right">
          <div className="bg-charcoal-deep border border-thunder/30 p-8 lg:p-10 backdrop-blur-sm">
            <div className="font-display text-electric tracking-[0.3em] text-xs mb-8">// SCHEDULE REQUEST</div>

            {status === 'sent' ? (
              <div className="text-center py-12">
                <div className="font-display text-thunder text-xl tracking-wider mb-3">✓ REQUEST RECEIVED</div>
                <p className="text-white/60">We'll confirm your appointment within one business hour.</p>
              </div>
            ) : (
              <form onSubmit={onSubmit} method="post" className="space-y-6">
                <Honeypot {...honeypotProps} id="schedule_company_website" />
                <div className="grid sm:grid-cols-2 gap-6">
                  <input className={inputClass} name="name" id="schedule_name" placeholder="FULL NAME" required autoComplete="name" value={form.name} onChange={onChange('name')} />
                  <input className={inputClass} name="phone" id="schedule_phone" placeholder="PHONE" type="tel" required autoComplete="tel" value={form.phone} onChange={onChange('phone')} />
                </div>

                <input className={inputClass} name="email" id="schedule_email" placeholder="EMAIL" type="email" required autoComplete="email" value={form.email} onChange={onChange('email')} />

                <div>
                  <label htmlFor="schedule_cleaning_type" className="font-display text-electric tracking-[0.3em] text-xs block mb-3">CLEANING TYPE</label>
                  <select className={inputClass} name="cleaning_type" id="schedule_cleaning_type" value={form.cleaningType} onChange={onChange('cleaningType')}>
                    {CLEANING_TYPES.map((t) => (
                      <option key={t} className="bg-charcoal-deep">{t}</option>
                    ))}
                  </select>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="schedule_date" className="font-display text-electric tracking-[0.3em] text-xs block mb-3">PREFERRED DATE</label>
                    <input
                      type="date"
                      name="preferred_date"
                      id="schedule_date"
                      className={inputClass}
                      required
                      value={form.date}
                      onChange={onChange('date')}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <span className="font-display text-electric tracking-[0.3em] text-xs block mb-3">PREFERRED TIME</span>
                    <div className="flex flex-col gap-2">
                      {TIME_SLOTS.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setForm({ ...form, time: t.id })}
                          className={radioClass(form.time === t.id)}
                          aria-pressed={form.time === t.id}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                    <input type="hidden" name="preferred_time" value={form.time} />
                  </div>
                </div>

                <input className={inputClass} name="address" id="schedule_address" placeholder="SERVICE ADDRESS" required autoComplete="street-address" value={form.address} onChange={onChange('address')} />

                <textarea
                  className={`${inputClass} min-h-[100px] resize-none`}
                  name="instructions"
                  id="schedule_instructions"
                  placeholder="SPECIAL INSTRUCTIONS (OPTIONAL)"
                  value={form.instructions}
                  onChange={onChange('instructions')}
                />

                <label htmlFor="schedule_sms_consent" className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    id="schedule_sms_consent"
                    name="sms_consent"
                    checked={smsConsent}
                    onChange={(e) => setSmsConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-thunder cursor-pointer flex-shrink-0"
                  />
                  <SmsConsentDisclosure />
                </label>

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full pulse-btn bg-thunder hover:bg-thunder-dark disabled:opacity-60 disabled:cursor-not-allowed text-charcoal-deep font-display font-bold py-5 tracking-[0.3em] text-sm transition"
                >
                  {status === 'sending' ? 'SENDING…' : 'SCHEDULE MY CLEANING →'}
                </button>
                {status === 'error' && (
                  <p className="text-red-400 text-sm text-center">Something broke. Call (407) 461-6039.</p>
                )}
              </form>
            )}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
